/**
 * useAuth Hook
 *
 * Manages authentication state using React Query.
 * Supports multi-company authentication with access/refresh tokens.
 * Supports multi-account and company switching.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, type LoginResponse, type User, type Company, type AccessibleCompany } from '../services/api/auth';
import { accountManager, type AccountSession } from '../utils/accountManager';

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthState {
  user: User | null;
  company: Company | null;
  accessibleCompanies: AccessibleCompany[];
  activeCompanyId: string | null;
  allAccounts: Array<{ email: string; user: User }>;
}

export function useAuth() {
  const queryClient = useQueryClient();

  // Helper to load all accounts list
  const loadAllAccounts = (): Array<{ email: string; user: User }> => {
    const accountsList = accountManager.getAllAccountsList();
    return accountsList.map(({ email, session }) => ({
      email,
      user: session.user,
    }));
  };

  // Check if user is authenticated (from accountManager)
  const { data: authState, isLoading } = useQuery<AuthState>({
    queryKey: ['auth', 'state'],
    queryFn: async () => {
      const session = accountManager.getActiveSession();

      if (!session) {
        return {
          user: null,
          company: null,
          accessibleCompanies: [],
          activeCompanyId: null,
          allAccounts: [],
        };
      }

      return {
        user: session.user,
        company: session.activeCompany,
        accessibleCompanies: session.accessibleCompanies || [],
        activeCompanyId: session.activeCompanyId,
        allAccounts: loadAllAccounts(),
      };
    },
    staleTime: Infinity, // Auth data doesn't change often
  });

  // Login mutation
  const loginMutation = useMutation<LoginResponse, Error, LoginCredentials>({
    mutationFn: (credentials) => authApi.login(credentials),
    onSuccess: async (response) => {
      if (response.success && response.data) {
        const { accessToken, refreshToken, expiresIn, user } = response.data;
        const expiresAt = Date.now() + expiresIn * 1000;
        const companyId = user.companyId || '';

        // Create account session (company will be populated after getCurrentCompany call)
        const session: AccountSession = {
          accessToken,
          refreshToken,
          expiresAt,
          user,
          activeCompanyId: companyId,
          activeCompany: null,
          accessibleCompanies: [],
        };

        // Save to accountManager
        accountManager.setAccount(user.email, session);
        accountManager.setActiveAccount(user.email);

        // Fetch accessible companies and current company with enabledPlugins
        const [companiesResponse, companyData] = await Promise.all([
          authApi.getAccessibleCompanies(),
          authApi.getCurrentCompany(),
        ]);

        if (companiesResponse.success && companiesResponse.data) {
          accountManager.updateAccessibleCompanies(companiesResponse.data.companies);
        }

        // Update with full company data (includes enabledPlugins)
        if (companyData) {
          const fullCompanyId = companyData.id || user.companyId || '';
          accountManager.updateActiveCompany(
            fullCompanyId,
            accessToken,
            refreshToken,
            expiresAt,
            companyData
          );
        }

        // Update React Query cache with fetched company data
        const updatedSession = accountManager.getActiveSession();
        queryClient.setQueryData(['auth', 'state'], {
          user,
          company: companyData || null,
          accessibleCompanies: updatedSession?.accessibleCompanies || [],
          activeCompanyId: companyData?.id || companyId,
          allAccounts: loadAllAccounts(),
        });
      }
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await authApi.logout();
    },
    onSuccess: () => {
      const email = accountManager.getActiveAccountEmail();
      if (email) {
        accountManager.removeAccount(email);
      }

      // Check if there are other accounts
      const remainingAccounts = accountManager.getAllAccountsList();
      if (remainingAccounts.length > 0) {
        window.location.reload();
      } else {
        queryClient.setQueryData(['auth', 'state'], {
          user: null,
          company: null,
          accessibleCompanies: [],
          activeCompanyId: null,
          allAccounts: [],
        });
        queryClient.clear();
      }
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation<
    { success: boolean; error?: string },
    Error,
    { currentPassword: string; newPassword: string }
  >({
    mutationFn: ({ currentPassword, newPassword }) =>
      authApi.changePassword(currentPassword, newPassword),
  });

  // Forgot password mutation (sends reset link to email)
  const forgotPasswordMutation = useMutation<
    { success: boolean; error?: string },
    Error,
    { email: string }
  >({
    mutationFn: ({ email }) => authApi.forgotPassword(email),
  });

  // Reset password mutation (uses token from reset link)
  const resetPasswordMutation = useMutation<
    { success: boolean; error?: string },
    Error,
    { token: string; newPassword: string }
  >({
    mutationFn: ({ token, newPassword }) =>
      authApi.resetPassword(token, newPassword),
  });

  // Switch company mutation
  const switchCompanyMutation = useMutation({
    mutationFn: async (companyId: string) => {
      const response = await authApi.switchCompany(companyId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to switch company');
      }
      return response;
    },
    onSuccess: (response) => {
      if (response.success && response.data) {
        const { accessToken, refreshToken, expiresIn, company, user: switchedUser } = response.data;
        const expiresAt = Date.now() + expiresIn * 1000;

        accountManager.updateActiveCompany(
          company.id,
          accessToken,
          refreshToken,
          expiresAt,
          company,
          switchedUser.permissions
        );

        // Reload page for clean state
        window.location.reload();
      }
    },
  });

  const user = authState?.user ?? null;
  const company = authState?.company ?? null;
  const accessibleCompanies = authState?.accessibleCompanies ?? [];
  const activeCompanyId = authState?.activeCompanyId ?? null;
  const allAccounts = authState?.allAccounts ?? [];
  const isPlatformAdmin = user?.roles?.includes('platform_admin') ?? false;

  const switchAccount = async (email: string) => {
    accountManager.setActiveAccount(email);

    // Return the account to its PRIMARY company, so switching to it behaves like
    // a fresh login (which is what works standalone/incognito). The stored
    // session may be parked in a previously company-switched secondary company,
    // where the account has reduced permissions — which otherwise surfaces as
    // "permission denied" errors even though the account is perfectly valid.
    const session = accountManager.getActiveSession();
    const primaryCompanyId = session?.user?.companyId;
    if (primaryCompanyId && session && session.activeCompanyId !== primaryCompanyId) {
      try {
        const response = await authApi.switchCompany(primaryCompanyId);
        if (response.success && response.data) {
          const { accessToken, refreshToken, expiresIn, company, user: switchedUser } = response.data;
          accountManager.updateActiveCompany(
            company.id,
            accessToken,
            refreshToken,
            Date.now() + expiresIn * 1000,
            company,
            switchedUser.permissions
          );
        }
      } catch {
        // Best-effort: if the reset fails the account still loads at its stored
        // company; the user can switch company manually.
      }
    }

    // Land on the default route (like a fresh login) rather than reloading the
    // current route, which the switched account may not have permission for.
    window.location.href = '/workspace';
  };

  const addAccount = () => {
    window.location.href = '/login?mode=addAccount';
  };

  const logoutAccount = async (email: string) => {
    await authApi.logout();
    accountManager.removeAccount(email);

    const activeEmail = accountManager.getActiveAccountEmail();
    if (!activeEmail || email === user?.email) {
      const remainingAccounts = accountManager.getAllAccountsList();
      if (remainingAccounts.length > 0) {
        window.location.reload();
      } else {
        queryClient.setQueryData(['auth', 'state'], {
          user: null,
          company: null,
          accessibleCompanies: [],
          activeCompanyId: null,
          allAccounts: [],
        });
        window.location.href = '/login';
      }
    } else {
      // Refresh accounts list
      queryClient.invalidateQueries({ queryKey: ['auth', 'state'] });
    }
  };

  const logoutAllAccounts = async () => {
    await authApi.logout();
    accountManager.clearAll();
    queryClient.setQueryData(['auth', 'state'], {
      user: null,
      company: null,
      accessibleCompanies: [],
      activeCompanyId: null,
      allAccounts: [],
    });
    queryClient.clear();
    window.location.href = '/login';
  };

  return {
    user,
    company,
    isLoading,
    isAuthenticated: !!user,
    isPlatformAdmin,
    // Multi-account support
    accessibleCompanies,
    activeCompanyId,
    allAccounts,
    // Actions
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    changePassword: changePasswordMutation.mutateAsync,
    forgotPassword: forgotPasswordMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    isForgotPasswordPending: forgotPasswordMutation.isPending,
    isResetPasswordPending: resetPasswordMutation.isPending,
    // Multi-account actions
    switchCompany: switchCompanyMutation.mutateAsync,
    switchAccount,
    addAccount,
    logoutAccount,
    logoutAllAccounts,
  };
}

export default useAuth;
